import { Tooltip } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { forwardRef } from "react";
import styles from "./m3.module.css";

const VARIANT_CLASS = {
  filled: styles.filled,
  tonal: styles.tonal,
  tonalPrimary: styles.tonalPrimary,
  outlined: styles.outlined,
  text: styles.text,
  danger: styles.danger,
  dangerFilled: styles.dangerFilled,
};

/**
 * Material 3 button.
 *
 * @param {"filled"|"tonal"|"tonalPrimary"|"outlined"|"text"|"danger"|"dangerFilled"} variant
 * @param {"sm"|"md"|"lg"} size
 * @param {React.ReactNode} icon - leading icon
 * @param {string} tooltip - shown on hover; also the aria-label for icon-only buttons
 * @param {string} disabledReason - tooltip shown instead while disabled, so users know why
 */
const M3Button = forwardRef(({
  children,
  variant = "filled",
  size = "md",
  icon = null,
  trailingIcon = null,
  loading = false,
  disabled = false,
  tooltip,
  disabledReason,
  className = "",
  type = "button",
  ...props
}, ref) => {
  const iconOnly = !children;
  const classes = [
    styles.btn,
    VARIANT_CLASS[variant] || styles.filled,
    size === "sm" ? styles.btnSm : size === "lg" ? styles.btnLg : "",
    iconOnly ? styles.btnIconOnly : "",
    className,
  ].join(" ");

  const button = (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-label={iconOnly ? tooltip : undefined}
      {...props}
    >
      {loading ? (
        <span className={styles.btnIcon}><AutorenewIcon className={styles.spin} /></span>
      ) : (
        icon && <span className={styles.btnIcon}>{icon}</span>
      )}
      {children && <span>{children}</span>}
      {trailingIcon && <span className={styles.btnIcon}>{trailingIcon}</span>}
    </button>
  );

  const title = disabled && disabledReason ? disabledReason : tooltip;
  if (!title) return button;
  // Disabled buttons fire no mouse events, so the tooltip wraps a span.
  return (
    <Tooltip title={title} arrow placement="top">
      <span style={{ display: "inline-flex" }}>{button}</span>
    </Tooltip>
  );
});

M3Button.displayName = "M3Button";

export default M3Button;
